import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Upload, Sparkles, Send, Loader } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { api } from '../lib/auth';
import { toast } from 'sonner';

const DEFAULT_QUESTIONS = [
  {
    id: 'goal',
    title: 'What is your primary goal?',
    aiPrompt: "Let's start with the win-condition. What should this accomplish?",
    type: 'text',
    placeholder: 'e.g., Generate more leads, increase bookings, launch a campaign...'
  },
  {
    id: 'audience',
    title: 'Who is your target audience?',
    aiPrompt: "Who are we speaking to? The more specific, the better.",
    type: 'text',
    placeholder: 'e.g., Small business owners, homeowners in Texas, B2B SaaS companies...'
  },
  {
    id: 'details',
    title: 'Any specific requirements or details?',
    aiPrompt: "Give me the details — what must be included or avoided?",
    type: 'textarea',
    placeholder: 'e.g., Brand colors, specific messaging, examples you like...'
  },
  {
    id: 'deadline',
    title: 'Do you have a target deadline?',
    aiPrompt: "When do you need this completed?",
    type: 'text',
    placeholder: 'e.g., Next week, by end of month, no rush...'
  }
];

export default function OrderBuilderPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isReviewing, setIsReviewing] = useState(false);

  const questions = service?.intake_template_json?.steps?.length 
    ? service.intake_template_json.steps 
    : DEFAULT_QUESTIONS;

  const progress = ((currentStep + 1) / (questions.length + 1)) * 100;

  useEffect(() => {
    loadService();
  }, [serviceId]);

  const loadService = async () => {
    try {
      const response = await api.getService(serviceId);
      setService(response.data);
    } catch (error) {
      toast.error('Service not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsReviewing(true);
    }
  };

  const handleBack = () => {
    if (isReviewing) {
      setIsReviewing(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const summary = Object.entries(answers)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');

      const response = await api.createOrder({
        service_id: serviceId,
        summary: summary || 'Order submitted via guided builder',
        structured_data_json: answers,
        files: []
      });

      toast.success('Mission submitted! We\'ll review it shortly.');
      navigate(`/orders/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const currentQuestion = questions[currentStep];

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="order-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <span className="text-sm text-gray-400">
              {isReviewing ? 'Review' : `Step ${currentStep + 1} of ${questions.length}`}
            </span>
          </div>
          <Progress value={isReviewing ? 100 : progress} className="h-2 bg-white/10" />
        </motion.div>

        {/* Service Info */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass rounded-xl p-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-white">{service?.title}</h2>
              <p className="text-sm text-gray-400">{service?.department_name}</p>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {isReviewing ? (
            /* Review Screen */
            <motion.div
              key="review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="glass rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-white">Review Your Mission Brief</h2>
                  <p className="text-sm text-gray-400">Make sure everything looks good</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {questions.map((q, i) => (
                  <div key={q.id} className="p-4 rounded-xl bg-white/5">
                    <p className="text-sm text-gray-400 mb-1">{q.title}</p>
                    <p className="text-white">{answers[q.id] || '(Not answered)'}</p>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 mb-6">
                <h3 className="font-semibold text-white mb-2">What happens next?</h3>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>1. Our team reviews your mission brief</li>
                  <li>2. We accept the order and prepare a proposal</li>
                  <li>3. You approve the proposal to start work</li>
                  <li>4. Track progress in real-time until completion</li>
                </ul>
              </div>

              <Button 
                onClick={handleSubmit}
                className="btn-primary w-full text-lg py-6"
                disabled={submitting}
                data-testid="submit-order-btn"
              >
                {submitting ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <>Submit Mission <Send className="ml-2 w-5 h-5" /></>
                )}
              </Button>
            </motion.div>
          ) : (
            /* Question Form */
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="glass rounded-2xl p-8"
            >
              {/* AI Prompt */}
              <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-primary font-medium mb-1">AI Concierge</p>
                  <p className="text-lg text-white">{currentQuestion?.aiPrompt || currentQuestion?.title}</p>
                </div>
              </div>

              {/* Input */}
              <div className="mb-8">
                <label className="block text-sm text-gray-400 mb-2">{currentQuestion?.title}</label>
                {currentQuestion?.type === 'textarea' ? (
                  <Textarea
                    value={answers[currentQuestion?.id] || ''}
                    onChange={(e) => setAnswers({ ...answers, [currentQuestion?.id]: e.target.value })}
                    placeholder={currentQuestion?.placeholder}
                    className="input-dark min-h-[150px]"
                    data-testid="order-input"
                  />
                ) : currentQuestion?.type === 'file' ? (
                  <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-primary/30 transition-colors cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400">Click to upload or drag files here</p>
                  </div>
                ) : (
                  <Input
                    type="text"
                    value={answers[currentQuestion?.id] || ''}
                    onChange={(e) => setAnswers({ ...answers, [currentQuestion?.id]: e.target.value })}
                    placeholder={currentQuestion?.placeholder}
                    className="input-dark"
                    data-testid="order-input"
                  />
                )}
              </div>

              {/* Navigation */}
              <div className="flex gap-4">
                <Button 
                  variant="outline"
                  onClick={() => handleNext()}
                  className="flex-1 btn-secondary"
                  data-testid="skip-btn"
                >
                  Skip
                </Button>
                <Button 
                  onClick={handleNext}
                  className="flex-1 btn-primary"
                  disabled={!answers[currentQuestion?.id]?.trim()}
                  data-testid="next-btn"
                >
                  {currentStep === questions.length - 1 ? 'Review' : 'Next'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
